import noop from "lib/noop"
import { useEffect, useRef } from "react"
import { Box, useTheme } from "@mui/material"
import { setCurrentCursorPosition } from "lib/@components/editable/setCurrentCursorPosition"
import { getCurrentCursorPosition } from "lib/@components/editable/getCurrentCursorPosition"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"

export function EditableBox({
    value,
    multiline,
    readOnly,
    placeholder,
    error,

    label = placeholder,
    onChange = noop,
    ...props
}) {
    const cursor = useRef(0)
    const ref = useRef()
    const theme = useTheme()
    useEffect(() => {
        try {
            if (document.activeElement === ref.current) {
                if (cursor.current) {
                    setCurrentCursorPosition(ref.current, cursor.current)
                }
            }
        } catch (e) {
            //
        }
    }, [value])

    const sharedProps = { minHeight: 10, dangerouslySetInnerHTML: { __html: value ?? "" } }
    const sharedSx = {
        ...props.sx,
        position: "relative",
        borderColor: error ? "error.main" : props.sx?.borderColor,
        "&:after":
            label &&
            (!value
                ? {
                      content: `"${label}"`,
                      position: "absolute",
                      color: "action.focus",
                      left: 0,
                      top: 0,
                  }
                : {
                      content: `"${label}"`,
                      position: "absolute",
                      color: "action.active",
                      left: 0,
                      fontSize: "75%",
                      top: -20,
                  }),
    }

    return (
        <If value={!!readOnly}>
            <Box {...props} sx={sharedSx} {...sharedProps} />
            <Else>
                <Box
                    ref={ref}
                    tabIndex={0}
                    contentEditable
                    onKeyDown={handleSpaceKey}
                    onInput={handleInput}
                    {...props}
                    sx={{
                        ...sharedSx,
                        "&:focus": {
                            outline: "1px solid",
                            outlineOffset: "4px",
                            outlineColor: error ? "error.main" : theme.palette.action.active,
                            borderRadius: 0.5,
                            transform: "translateY(-1px)",
                            border: "none !important",
                        },
                    }}
                    {...sharedProps}
                />
            </Else>
        </If>
    )

    function handleSpaceKey(event) {
        if (event.code === "Enter") {
            if (!multiline) {
                event.preventDefault()
                event.stopPropagation()
                return
            }
        }
        if (event.code !== "Space") {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        document.execCommand("insertText", false, " ")
    }

    function handleInput(event) {
        cursor.current = getCurrentCursorPosition(ref.current.id)
        onChange(event.target.innerText)
    }
}
